import { useMemo, useState } from "react";
import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { PawWatermark } from "./Paws";
import { pricing, hours, notices, transport } from "../data/siteConfig";
import { Clock, CalendarDays, Megaphone, Minus, Plus, Car } from "lucide-react";

const RateCard = ({ label, sub, rate, peak }) => (
    <div
        className={`rounded-3xl border p-8 shadow-sm transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md ${
            peak
                ? "border-fern/50 bg-fernlight"
                : "border-stone-200 bg-cream"
        }`}
        data-testid={peak ? "rate-card-peak" : "rate-card-standard"}
    >
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-fern">
            {label}
        </p>
        <p className="mt-4 font-serif text-6xl font-light text-[#1c1917] sm:text-7xl">
            <span className="align-top font-mono text-xl text-stone-400">
                {pricing.currency}
            </span>
            {rate}
        </p>
        <p className="mt-2 text-sm text-stone-600">{sub}</p>
    </div>
);

export const Pricing = () => {
    const [dropoff, setDropoff] = useState("");
    const [collect, setCollect] = useState("");
    const [cats, setCats] = useState(1);
    // Medication: administrations per day, per cat (0 = none, 3 = "3 or more")
    const [meds, setMeds] = useState({});

    const setMed = (i, v) => setMeds((m) => ({ ...m, [i]: v }));

    const estimate = useMemo(() => {
        if (!dropoff || !collect) return null;
        const d0 = new Date(`${dropoff}T12:00:00`);
        const d1 = new Date(`${collect}T12:00:00`);
        if (isNaN(d0) || isNaN(d1) || d1 < d0) return { error: true };
        let peak = 0;
        let std = 0;
        const d = new Date(d0);
        let guard = 0;
        while (d <= d1 && guard < 400) {
            if (pricing.peakMonths.includes(d.getMonth())) peak += 1;
            else std += 1;
            d.setDate(d.getDate() + 1);
            guard += 1;
        }
        const days = peak + std;
        const boarding =
            (peak * pricing.peakRate + std * pricing.standardRate) * cats;
        const medPerDay = Array.from(
            { length: cats },
            (_, i) => meds[i] ?? 0
        ).reduce((a, b) => a + b, 0);
        const med = medPerDay * pricing.medicationRate * days;
        return {
            days,
            peak,
            std,
            medPerDay,
            med,
            total: boarding + med,
        };
    }, [dropoff, collect, cats, meds]);

    const inputCls =
        "mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-sm text-[#1c1917]";

    return (
        <section
            className="relative overflow-hidden bg-white py-24 text-[#1c1917] sm:py-32"
            data-testid="pricing-section"
        >
            <PawWatermark tone="light" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <Chapter
                    no="04"
                    id="pricing"
                    eyebrow="Prices & Hours"
                    title="Rates & hours."
                />

                <div className="mt-16 grid gap-5 sm:grid-cols-2">
                    <Reveal>
                        <RateCard
                            label="Standard rate"
                            rate={pricing.standardRate}
                            sub="per cat, per calendar day — February to November"
                        />
                    </Reveal>
                    <Reveal delay={0.08}>
                        <RateCard
                            peak
                            label={`Peak season — ${pricing.peakLabel}`}
                            rate={pricing.peakRate}
                            sub="per cat, per calendar day — Christmas and summer holidays"
                        />
                    </Reveal>
                </div>

                <div className="mt-6 grid gap-5 lg:grid-cols-2">
                    {/* Calendar day explainer */}
                    <Reveal delay={0.1}>
                        <div
                            className="h-full rounded-3xl border border-stone-200 bg-cream p-8"
                            data-testid="calendar-day-explainer"
                        >
                            <CalendarDays
                                className="h-7 w-7 text-fern"
                                aria-hidden="true"
                            />
                            <h3 className="mt-4 font-serif text-2xl font-semibold">
                                Charged by calendar day
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-stone-600 sm:text-base">
                                We charge by the calendar day, not by 24-hour
                                periods. Drop off on Monday and collect on
                                Tuesday, and that's{" "}
                                <strong className="text-[#1c1917]">
                                    two chargeable days
                                </strong>{" "}
                                — whatever the drop-off and collection times.
                            </p>
                        </div>
                    </Reveal>

                    {/* Stay calculator */}
                    <Reveal delay={0.15}>
                        <div
                            className="h-full rounded-3xl border border-fern/40 bg-fernlight p-8"
                            data-testid="stay-calculator"
                        >
                            <h3 className="font-serif text-2xl font-semibold">
                                Estimate your cat's stay
                            </h3>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                <label className="block">
                                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                        Drop-off
                                    </span>
                                    <input
                                        type="date"
                                        value={dropoff}
                                        onChange={(e) => setDropoff(e.target.value)}
                                        data-testid="calc-dropoff-input"
                                        className={inputCls}
                                    />
                                </label>
                                <label className="block">
                                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                        Collection
                                    </span>
                                    <input
                                        type="date"
                                        value={collect}
                                        onChange={(e) => setCollect(e.target.value)}
                                        data-testid="calc-collect-input"
                                        className={inputCls}
                                    />
                                </label>
                            </div>
                            <div className="mt-4 flex items-center justify-between rounded-xl border border-stone-300 bg-white px-4 py-3">
                                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                    Cats
                                </span>
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setCats(Math.max(1, cats - 1))}
                                        aria-label="Fewer cats"
                                        data-testid="calc-cats-minus"
                                        className="rounded-full border border-stone-300 p-1.5 transition-colors duration-200 hover:border-fern hover:text-fern"
                                    >
                                        <Minus className="h-4 w-4" />
                                    </button>
                                    <span
                                        className="w-6 text-center font-serif text-2xl"
                                        data-testid="calc-cats-count"
                                    >
                                        {cats}
                                    </span>
                                    <button
                                        onClick={() => setCats(Math.min(8, cats + 1))}
                                        aria-label="More cats"
                                        data-testid="calc-cats-plus"
                                        className="rounded-full border border-stone-300 p-1.5 transition-colors duration-200 hover:border-fern hover:text-fern"
                                    >
                                        <Plus className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Medication — per cat, administrations per day */}
                            <div className="mt-4 space-y-3" data-testid="calc-meds">
                                {Array.from({ length: cats }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between gap-3 rounded-xl border border-stone-300 bg-white px-4 py-3"
                                    >
                                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                                            {cats > 1
                                                ? `Cat ${i + 1} medication`
                                                : "Medication"}
                                        </span>
                                        <select
                                            value={meds[i] ?? 0}
                                            onChange={(e) =>
                                                setMed(i, Number(e.target.value))
                                            }
                                            aria-label={
                                                cats > 1
                                                    ? `Medication administrations per day for cat ${i + 1}`
                                                    : "Medication administrations per day"
                                            }
                                            data-testid={`calc-meds-select-${i}`}
                                            className="rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-sm text-[#1c1917]"
                                        >
                                            <option value={0}>None</option>
                                            <option value={1}>1× / day</option>
                                            <option value={2}>2× / day</option>
                                            <option value={3}>3+ / day</option>
                                        </select>
                                    </div>
                                ))}
                                <p className="text-xs text-stone-500">
                                    {pricing.currency}
                                    {pricing.medicationRate} per administration,
                                    per cat. 3+ charged as 3 per day.
                                </p>
                            </div>

                            <div
                                className="mt-5 rounded-xl bg-white p-5"
                                data-testid="calc-result"
                            >
                                {!estimate && (
                                    <p className="text-sm text-stone-500">
                                        Choose your dates to see an estimate.
                                    </p>
                                )}
                                {estimate?.error && (
                                    <p className="text-sm text-red-600">
                                        Collection date needs to be on or after
                                        the drop-off date.
                                    </p>
                                )}
                                {estimate && !estimate.error && (
                                    <>
                                        <p className="font-serif text-4xl text-[#1c1917]">
                                            {pricing.currency}
                                            {estimate.total}
                                            <span className="ml-2 font-sans text-sm text-stone-500">
                                                estimated total
                                            </span>
                                        </p>
                                        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500">
                                            {estimate.days} calendar day
                                            {estimate.days === 1 ? "" : "s"} ×{" "}
                                            {cats} cat{cats === 1 ? "" : "s"}
                                            {estimate.peak > 0 &&
                                                ` · ${estimate.peak} peak day${estimate.peak === 1 ? "" : "s"}`}
                                        </p>
                                        {estimate.med > 0 && (
                                            <p
                                                className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-fern"
                                                data-testid="calc-med-line"
                                            >
                                                Includes {pricing.currency}
                                                {estimate.med} medication ·{" "}
                                                {estimate.medPerDay} admin
                                                {estimate.medPerDay === 1
                                                    ? ""
                                                    : "s"}
                                                /day × {estimate.days} day
                                                {estimate.days === 1 ? "" : "s"}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Hours & notices */}
                <div className="mt-6 grid gap-5 lg:grid-cols-2">
                    <Reveal delay={0.1}>
                        <div
                            className="h-full rounded-3xl border border-stone-200 bg-cream p-8"
                            data-testid="hours-card"
                        >
                            <Clock
                                className="h-7 w-7 text-fern"
                                aria-hidden="true"
                            />
                            <h3 className="mt-4 font-serif text-2xl font-semibold">
                                Drop-off & collection hours
                            </h3>
                            <dl className="mt-5 space-y-3 text-sm sm:text-base">
                                <div className="flex justify-between gap-4 border-b border-stone-200 pb-3">
                                    <dt className="text-stone-500">Morning</dt>
                                    <dd className="font-mono text-[#1c1917]" data-testid="hours-morning">
                                        {hours.morning}
                                    </dd>
                                </div>
                                <div className="flex justify-between gap-4 border-b border-stone-200 pb-3">
                                    <dt className="text-stone-500">Afternoon</dt>
                                    <dd className="font-mono text-[#1c1917]" data-testid="hours-afternoon">
                                        {hours.afternoon}
                                    </dd>
                                </div>
                                <div className="flex justify-between gap-4 border-b border-stone-200 pb-3">
                                    <dt className="text-stone-500">Days</dt>
                                    <dd className="font-mono text-[#1c1917]">{hours.days}</dd>
                                </div>
                                <div className="flex justify-between gap-4">
                                    <dt className="text-stone-500">Closed</dt>
                                    <dd className="text-right font-mono text-[#1c1917]" data-testid="hours-closed">
                                        {hours.closedDays.join(" & ")}
                                    </dd>
                                </div>
                            </dl>
                            <p className="mt-5 text-xs leading-relaxed text-stone-500">
                                {hours.note}
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div
                            className="h-full rounded-3xl border border-stone-200 bg-cream p-8"
                            data-testid="notices-card"
                        >
                            <Megaphone
                                className="h-7 w-7 text-fern"
                                aria-hidden="true"
                            />
                            <h3 className="mt-4 font-serif text-2xl font-semibold">
                                Notices & upcoming closures
                            </h3>
                            {notices.length === 0 ? (
                                <p
                                    className="mt-4 text-sm leading-relaxed text-stone-600 sm:text-base"
                                    data-testid="notices-empty"
                                >
                                    No upcoming closure notices — we're open as
                                    usual. Plans changed?{" "}
                                    <a
                                        href="#book"
                                        className="text-fern underline underline-offset-4 transition-colors duration-200 hover:text-[#1c1917]"
                                        data-testid="notices-contact-link"
                                    >
                                        Contact us
                                    </a>{" "}
                                    — we'll always do our best to help, subject
                                    to space and availability.
                                </p>
                            ) : (
                                <ul className="mt-4 space-y-3" data-testid="notices-list">
                                    {notices.map((n, i) => (
                                        <li
                                            key={i}
                                            className="rounded-xl border border-fern/40 bg-fernlight p-4"
                                            data-testid={`notice-${i}`}
                                        >
                                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-fern">
                                                {n.date}
                                            </p>
                                            <p className="mt-1 text-sm text-stone-700">
                                                {n.text}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Reveal>
                </div>

                {/* Collection, drop-off & transport */}
                <Reveal delay={0.2}>
                    <div
                        className="mt-5 rounded-3xl border border-stone-200 bg-cream p-8"
                        data-testid="transport-card"
                    >
                        <Car className="h-7 w-7 text-fern" aria-hidden="true" />
                        <h3 className="mt-4 font-serif text-2xl font-semibold">
                            Collection, drop-off &amp; transport
                        </h3>
                        <div className="mt-5 grid gap-8 sm:grid-cols-2">
                            <div>
                                <p className="text-sm leading-relaxed text-stone-600 sm:text-base">
                                    {transport.intro}
                                </p>
                                <dl className="mt-5 space-y-3 text-sm sm:text-base">
                                    {transport.rates.map((r) => (
                                        <div
                                            key={r.area}
                                            className="flex justify-between gap-4 border-b border-stone-200 pb-3"
                                        >
                                            <dt className="text-stone-500">
                                                {r.area}
                                            </dt>
                                            <dd
                                                className="text-right font-mono text-[#1c1917]"
                                                data-testid={`transport-rate-${r.area === "Karori" ? "karori" : "wellington"}`}
                                            >
                                                {pricing.currency}
                                                {r.eachWay} each way ·{" "}
                                                {pricing.currency}
                                                {r.ret} return
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                                <p className="mt-4 text-xs leading-relaxed text-stone-500">
                                    {transport.vetNote}
                                </p>
                            </div>
                            <ul className="space-y-2.5 text-sm leading-relaxed text-stone-600 sm:text-base">
                                {transport.conditions.map((c, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-3"
                                        data-testid={`transport-condition-${i}`}
                                    >
                                        <span
                                            className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-fern"
                                            aria-hidden="true"
                                        />
                                        {c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
